import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { Company } from '@/types/company';
import { fetchCompanies, getDataMode } from '@/lib/data-service';
import { useUserPreferencesSync } from '@/hooks/use-user-preferences-sync';
import { setAuthCompanyId } from '@/lib/auth-fetch';
import { useDataMode } from '@/contexts/DataModeContext';
import { getCurrentUserCompanyAccess } from '@/services/integrationsRedesignService';
import { consumeDeepLinkCompanyId } from '@/lib/deep-link-utils';

interface CompanyContextType {
  currentCompany: Company | null;
  availableCompanies: Company[];
  isLoading: boolean;
  error: string | null;
  switchCompany: (companyId: string) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  /**
   * Silently re-fetch the company list without resetting loading state.
   * Designed to be called when the company selector popover is opened so
   * that newly-granted (or revoked) company access is reflected immediately.
   */
  refreshCompanyList: () => Promise<void>;
  /**
   * Merges a partial Company update into `currentCompany`.
   * Used by `useCompanyDetails` to push saved invoice details (name, logo,
   * address, bank details, etc.) back into context without triggering a full
   * company reload.
   */
  updateCurrentCompany: (details: Partial<Company>) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

const STORAGE_KEY = 'ebprojects_current_company';

interface CompanyProviderProps {
  children: ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { loadPreferences, saveSelectedCompany } = useUserPreferencesSync();
  const { mode } = useDataMode();

  const loadCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // fetchCompanies handles demo/live mode switching internally
      const companies = await fetchCompanies() as Company[];

      // Defence-in-depth: ensure only active companies appear in the selector
      // regardless of the data source (live API, demo mock, tour mock).
      let activeCompanies = companies.filter(c => c.status === 'active');

      // In live mode, filter companies by the current user's access list
      const dataMode = getDataMode();
      if (dataMode === 'live') {
        try {
          const myAccess = await getCurrentUserCompanyAccess();
          const accessibleIds = new Set(
            myAccess.filter((a) => a.hasAccess).map((a) => a.companyId)
          );

          activeCompanies = activeCompanies.filter((c) => accessibleIds.has(c.id));

          // Determine the user's default company from the access list
          const defaultAccess = myAccess.find((a) => a.isDefault);
          if (defaultAccess) {
            const defaultCompany = activeCompanies.find((c) => c.id === defaultAccess.companyId);
            if (defaultCompany) {
              activeCompanies = [
                defaultCompany,
                ...activeCompanies.filter((c) => c.id !== defaultCompany.id),
              ];
            }
          }
        } catch (accessErr) {
          // Graceful degradation: if the access endpoint fails, show all companies
          console.warn('Could not fetch user company access, showing all companies:', accessErr);
        }
      }

      setAvailableCompanies(activeCompanies);

      // Try to restore from backend first (in live mode), then fallback to local storage
      let companyToLoad: Company | null = null;

      // Deep-link company takes highest priority — the notification link
      // includes `?companyId=X` so we must open that specific company.
      let deepLinkCompanyId: string | null = null;
      try {
        const urlParams = new URLSearchParams(window.location.search);
        deepLinkCompanyId = urlParams.get('companyId');
      } catch { /* URL parsing unavailable */ }
      if (!deepLinkCompanyId) {
        deepLinkCompanyId = consumeDeepLinkCompanyId();
      }
      if (deepLinkCompanyId) {
        companyToLoad = activeCompanies.find((c) => String(c.id) === deepLinkCompanyId) || null;
      }
      
      if (!companyToLoad && dataMode === 'live') {
        // Try to load from backend preferences
        const backendPrefs = await loadPreferences();
        if (backendPrefs?.selectedCompanyId) {
          companyToLoad = activeCompanies.find((c) => c.id === backendPrefs.selectedCompanyId) || null;
        }
      }
      
      // Fallback to local storage if backend didn't have a selection
      if (!companyToLoad) {
        const storedCompanyId = localStorage.getItem(STORAGE_KEY);
        if (storedCompanyId) {
          companyToLoad = activeCompanies.find((c) => String(c.id) === storedCompanyId) || null;
        }
      }

      // If no stored company or stored company not found, use first available
      if (!companyToLoad && activeCompanies.length > 0) {
        companyToLoad = activeCompanies[0];
      }

      if (companyToLoad) {
        setAuthCompanyId(String(companyToLoad.id));
        setCurrentCompany(companyToLoad);
        localStorage.setItem(STORAGE_KEY, String(companyToLoad.id));
      } else {
        setError('No companies available');
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
      setError('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, [loadPreferences]);

  // Load companies on mount and when data mode changes
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies, mode]);

  useEffect(() => {
    setAuthCompanyId(currentCompany ? String(currentCompany.id) : undefined);
  }, [currentCompany]);

  const switchCompany = useCallback(
    async (companyId: string) => {
      try {
        setIsLoading(true);
        setError(null);

        const company = availableCompanies.find((c) => String(c.id) === companyId);
        if (!company) {
          throw new Error('Company not found or access denied');
        }

        setAuthCompanyId(String(company.id));

        setCurrentCompany(company);
        localStorage.setItem(STORAGE_KEY, companyId);

        const dataMode = getDataMode();
        if (dataMode === 'live') {
          saveSelectedCompany(company.id).catch((err) => {
            console.error('Failed to save selected company to backend:', err);
          });
        }

        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId } }));
      } catch (err) {
        console.error('Failed to switch company:', err);
        setError('Failed to switch company');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [availableCompanies, saveSelectedCompany]
  );

  const refreshCompanies = useCallback(async () => {
    await loadCompanies();
  }, [loadCompanies]);

  /**
   * Silently re-fetch the company list without resetting loading state.
   * Called when the company selector popover opens so that newly-granted
   * (or revoked) company access is reflected immediately.
   */
  const refreshCompanyList = useCallback(async () => {
    try {
      const companies = await fetchCompanies() as Company[];
      let activeCompanies = companies.filter(c => c.status === 'active');

      const dataMode = getDataMode();
      if (dataMode === 'live') {
        try {
          const myAccess = await getCurrentUserCompanyAccess();
          const accessibleIds = new Set(
            myAccess.filter((a) => a.hasAccess).map((a) => a.companyId)
          );

          activeCompanies = activeCompanies.filter((c) => accessibleIds.has(c.id));

          const defaultAccess = myAccess.find((a) => a.isDefault);
          if (defaultAccess) {
            const defaultCompany = activeCompanies.find((c) => c.id === defaultAccess.companyId);
            if (defaultCompany) {
              activeCompanies = [
                defaultCompany,
                ...activeCompanies.filter((c) => c.id !== defaultCompany.id),
              ];
            }
          }
        } catch (accessErr) {
          console.warn('Could not fetch user company access, showing all companies:', accessErr);
        }
      }

      setAvailableCompanies(activeCompanies);

      // If the current company still exists, update it with fresh data
      // (e.g. newly-configured integration fields like timesheetInstanceId).
      // If it was revoked, fall back to the first available company.
      if (currentCompany) {
        const updated = activeCompanies.find((c) => c.id === currentCompany.id);
        if (updated) {
          setCurrentCompany(updated);
        } else {
          const fallback = activeCompanies[0] || null;
          setCurrentCompany(fallback);
          if (fallback) {
            setAuthCompanyId(String(fallback.id));
            localStorage.setItem(STORAGE_KEY, String(fallback.id));
            window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId: String(fallback.id) } }));
          }
        }
      }
    } catch (err) {
      // Silently keep the existing list on failure
      console.warn('Silent company list refresh failed:', err);
    }
  }, [currentCompany]);

  const updateCurrentCompany = useCallback((details: Partial<Company>) => {
    setCurrentCompany((prev) => {
      if (!prev) return prev;
      return { ...prev, ...details };
    });
  }, []);

  const value: CompanyContextType = {
    currentCompany,
    availableCompanies,
    isLoading,
    error,
    switchCompany,
    refreshCompanies,
    refreshCompanyList,
    updateCurrentCompany,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany(): CompanyContextType {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
